import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    Typography,
    Box,
} from '@mui/material';
import { Warning, RestartAlt } from '@mui/icons-material';
import { useNotify, useRefresh, useDataProvider } from 'react-admin';
import { apiClient } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';

interface RestartServerDialogProps {
    open: boolean;
    serverId: string;
    serverName: string;
    onClose: () => void;
    onSuccess?: () => void;
}

const RestartServerDialog: React.FC<RestartServerDialogProps> = ({
    open,
    serverId,
    serverName,
    onClose,
    onSuccess,
}) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    const handleRestart = async () => {
        try {
            setLoading(true);

            const trimmedReason = reason.trim();
            const response = await apiClient.post(
                `${API_CONFIG.endpoints.backend.ocppServers}/${serverId}/restart`,
                trimmedReason ? { reason: trimmedReason } : {},
            ) as { success?: boolean; server_id?: string; restart_initiated_at?: string };

            if (!response.success) {
                notify('Restart request failed', { type: 'error' });
                return;
            }

            notify('Restart initiated. Polling for the server to come back online...', { type: 'info' });
            onClose();

            // Snapshot last_heartbeat BEFORE restart; poll for advance up to 90s.
            // The OCPP server takes ~10-30s to restart and re-activate; the
            // backend activation handler updates last_heartbeat on re-activation.
            const startedAt = Date.now();
            let prevHeartbeat: string | null = null;
            try {
                const initial = await dataProvider.getOne('ocpp-servers', { id: serverId });
                prevHeartbeat = (initial?.data as any)?.last_heartbeat || null;
            } catch (_) {}

            const pollInterval = setInterval(async () => {
                try {
                    const fresh = await dataProvider.getOne('ocpp-servers', { id: serverId });
                    const currentHeartbeat = (fresh?.data as any)?.last_heartbeat || null;
                    if (currentHeartbeat && currentHeartbeat !== prevHeartbeat) {
                        clearInterval(pollInterval);
                        notify('OCPP server reconnected — restart complete', { type: 'success' });
                        refresh();
                        if (onSuccess) onSuccess();
                        return;
                    }
                    if (Date.now() - startedAt > 90_000) {
                        clearInterval(pollInterval);
                        notify('Server did not reconnect within 90s — check container logs', { type: 'warning' });
                    }
                } catch (_) {
                    // Transient error during poll — keep trying until timeout
                }
            }, 3_000);
        } catch (error: any) {
            console.error('Error restarting OCPP server:', error);
            const errMsg =
                error?.body?.error ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to restart OCPP server';
            notify(errMsg, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (loading) return;
        setReason('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RestartAlt color="warning" />
                Restart OCPP Server
            </DialogTitle>
            <DialogContent>
                <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
                    Server <strong>{serverName}</strong> will be gracefully restarted.
                </Alert>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Expect a ~10-30s blackout window while the server shuts down and reconnects.
                    Active charging sessions are <strong>safe</strong> — chargers reconnect via WebSocket
                    after the restart and resume sending meter values automatically.
                    Charging profiles persist on the chargers via their validity windows.
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    During the blackout, RemoteStart / RemoteStop / Reboot commands from admin
                    will fail until the server reconnects.
                </Typography>
                <Box>
                    <TextField
                        label="Reason (optional)"
                        helperText="Optional — recorded in the audit log"
                        fullWidth
                        multiline
                        rows={2}
                        value={reason}
                        onChange={(e) => setReason(e.target.value.slice(0, 500))}
                        disabled={loading}
                        inputProps={{ maxLength: 500 }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleRestart}
                    disabled={loading}
                    variant="contained"
                    color="warning"
                    startIcon={<RestartAlt />}
                >
                    {loading ? 'Restarting…' : 'Restart Server'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RestartServerDialog;
